import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useContext(AuthContext);
  const [stats, setStats] = useState({
    total_contracts: 0,
    active_contracts: 0,
    expiring_soon: 0,
    expired_contracts: 0,
    total_amount: 0
  });
  const [recentContracts, setRecentContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsResponse, contractsResponse] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/contracts`)
      ]);

      setStats(statsResponse.data);
      // Get the 5 most recent contracts
      const sortedContracts = contractsResponse.data
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      setRecentContracts(sortedContracts);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusBadge = (status) => {
    const configs = {
      'actif': { 
        label: 'Actif',
        className: 'status-actif',
        icon: '●'
      },
      'expiré': { 
        label: 'Expiré',
        className: 'status-expiré',
        icon: '⚠'
      },
      'en_renouvellement': { 
        label: 'Renouvellement',
        className: 'status-en_renouvellement',
        icon: '⟲'
      }
    };

    const config = configs[status] || configs['actif'];
    return (
      <span className={`status-badge ${config.className}`}>
        <span className="mr-1">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary">Dashboard</h1>
          <p className="text-text-muted mt-1">
            Bienvenue, {user?.full_name} 
            <span className="font-mono text-xs ml-2 px-2 py-1 bg-bg-muted rounded">
              {new Date().toLocaleString('fr-FR', { 
                day: '2-digit', 
                month: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </p>
        </div>
        <Link to="/contracts/new">
          <Button className="btn-primary" data-testid="new-contract-button">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nouveau contrat
          </Button>
        </Link>
      </div>

      {/* Stats Cards - Une ligne horizontale compacte */}
      <div className="dashboard-stats-grid">
        {/* Combined Active/Total Card */}
        <Card className="stats-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-text-muted mb-1" data-testid="total-contracts-label">Total / Actifs</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-text-primary" data-testid="total-contracts-value">{stats.total_contracts}</span>
                  <span className="text-lg font-semibold text-success" data-testid="active-contracts-value">
                    / {stats.active_contracts}
                  </span>
                </div>
                <p className="text-xs text-text-muted font-mono">
                  {stats.active_contracts > 0 ? `${((stats.active_contracts / stats.total_contracts) * 100).toFixed(0)}% actifs` : 'Aucun actif'}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alerts Card */}
        <Card className="stats-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-text-muted mb-1" data-testid="expiring-soon-label">Alertes</p>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <span className="text-lg font-bold text-warning" data-testid="expiring-soon-value">{stats.expiring_soon}</span>
                    <span className="text-xs text-warning">⟲</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-lg font-bold text-error" data-testid="expired-contracts-value">{stats.expired_contracts}</span>
                    <span className="text-xs text-error">⚠</span>
                  </div>
                </div>
                <p className="text-xs text-text-muted font-mono">
                  {stats.expiring_soon + stats.expired_contracts > 0 ? 'Action requise' : 'Tout est OK'}
                </p>
              </div>
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                {stats.expiring_soon + stats.expired_contracts > 0 ? (
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Financial Card */}
        <Card className="stats-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-text-muted mb-1">Valeur Portfolio</p>
                <p className="text-xl font-bold text-primary" data-testid="total-amount-value">
                  {formatCurrency(stats.total_amount)}
                </p>
                <p className="text-xs text-text-muted font-mono">
                  {stats.total_contracts > 0 ? `~${formatCurrency(stats.total_amount / stats.total_contracts)} /contrat` : 'N/A'}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Contracts */}
      <div>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Activité récente</CardTitle>
              <div className="flex items-center gap-4">
                <span className="text-xs text-text-muted font-mono">
                  Last sync: {new Date().toLocaleTimeString()}
                </span>
                <Link to="/contracts">
                  <Button variant="ghost" size="sm" data-testid="view-all-contracts-link">
                    Voir tout →
                  </Button>
                </Link>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {recentContracts.length > 0 ? (
              <div className="space-y-3">
                {recentContracts.map((contract) => (
                  <div key={contract.id} className="flex items-center justify-between p-3 bg-bg-subtle rounded-lg hover:bg-bg-muted transition-colors border-l-3 border-l-primary/30" data-testid={`recent-contract-${contract.id}`}>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-semibold text-text-primary">{contract.name}</h4>
                        <span className="text-xs text-text-muted font-mono">#{contract.id.slice(-6)}</span>
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <p className="text-sm text-text-muted">{contract.supplier}</p>
                        <p className="text-xs text-text-muted">→ {formatDate(contract.end_date)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {getStatusBadge(contract.status)}
                      <p className="text-sm font-semibold text-text-primary mt-1">{formatCurrency(contract.amount)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-text-muted" data-testid="no-recent-contracts">
                <svg className="w-12 h-12 mx-auto mb-4 text-text-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-lg font-medium mb-2">Aucun contrat</p>
                <p className="text-sm mb-4">Commencez par créer votre premier contrat</p>
                <Link to="/contracts/new">
                  <Button className="btn-primary" data-testid="create-first-contract-button">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Créer un contrat
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}